import {
  queryOptions,
  useQuery,
  type UseQueryResult,
} from '@tanstack/react-query'

import { buildMemberRetirementAges } from '#calculator/memberRetirementAges'
import { fetchLatestSurveyConfig } from '#configuration/fetchLatestSurveyConfig'
import { getFilingStatus } from '#dictionary/filingStatuses'
import { FeatureFlag } from '#featureFlags/schema'
import { useIsFeatureFlagEnabled } from '#featureFlags/useIsFeatureFlagEnabled'
import { useShowProfileQuery } from '#profile/api'
import ProfileService from '#profile/ProfileService'
import type { ProfileDoc } from '#profile/schemas'

import parseSurveyListConfig from './parseSurveyListConfig'
import type { Survey } from './schemas/survey'
import type { Topic } from './schemas/topic'
import surveyListRaw from './surveyList/surveyList.yml?raw'
import {
  initSurveyVariables,
  type SurveyVariables,
  type SurveyVariablesProfileInput,
} from './surveyVariables'

const profileToVariablesInput = (
  profile: ProfileService
): SurveyVariablesProfileInput => ({
  maritalStatus: profile.maritalStatus,
  filingStatus: getFilingStatus(profile),
  minorChildrenCount: profile.minorChildren.length,
  otherDependentsCount: profile.otherDependents.length,
  projectionStartYear: profile.projectionStartYear,
  person: profile.person,
  partner: profile.partner,
})

const parseSurveys = (yamlContent: string): Survey[] => {
  const result = parseSurveyListConfig(yamlContent)
  if (result.isValid) {
    return result.data.surveys
  }
  throw result.errors[0]
}

const stableSurveysQueryOptions = queryOptions({
  queryKey: ['global', 'surveys', { source: 'bundle' }],
  queryFn: (): Promise<Survey[]> =>
    Promise.resolve(parseSurveys(surveyListRaw)),
  staleTime: Infinity,
  gcTime: Infinity,
  retry: false,
})

const betaSurveysQueryOptions = queryOptions({
  queryKey: ['global', 'surveys', { source: 'firestore' }],
  queryFn: async (): Promise<Survey[]> => {
    const content = await fetchLatestSurveyConfig()
    return parseSurveys(content)
  },
})

const useListSurveysQueryOptions = () => {
  const isBeta = useIsFeatureFlagEnabled(FeatureFlag.BetaSurvey)
  return isBeta ? betaSurveysQueryOptions : stableSurveysQueryOptions
}

export const useListSurveysQuery = (): UseQueryResult<Survey[], Error> => {
  const options = useListSurveysQueryOptions()
  return useQuery(options)
}

export const useShowSurveyQuery = (
  surveyId: string | undefined
): UseQueryResult<Survey | undefined, Error> => {
  const options = useListSurveysQueryOptions()
  return useQuery({
    ...options,
    enabled: !!surveyId,
    select: list => list.find(s => s.id === surveyId),
  })
}

export type SurveyTopic = {
  survey: Survey
  topic: Topic
}

export const useShowSurveyTopicQuery = (
  topicId: string | undefined
): UseQueryResult<SurveyTopic | undefined, Error> => {
  const options = useListSurveysQueryOptions()
  return useQuery({
    ...options,
    enabled: !!topicId,
    select: surveys => {
      for (const survey of surveys) {
        const topic = survey.topics.find(g => g.id === topicId)
        if (topic) {
          return {
            survey,
            topic,
          }
        }
      }
      return undefined
    },
  })
}

export const useShowSurveyVariablesQuery = (): UseQueryResult<
  SurveyVariables,
  Error
> =>
  useShowProfileQuery('default', {
    select: (data: ProfileDoc) => {
      const profile = new ProfileService(data)
      return initSurveyVariables(
        profileToVariablesInput(profile),
        buildMemberRetirementAges(profile.allMembers)
      )
    },
  })
